import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { LOGO, NAV_LINKS } from "../../data/content";
import { scrollToId } from "../../lib/scroll";

const CTA_URL =
  process.env.REACT_APP_GOLEE_URL ||
  "https://moduli.golee.it/polisportiva-tricesimo/tesseramento-alla-societ";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to a section on the home page. If we're on another route, go home first.
  const goSection = (id) => {
    setOpen(false);
    if (isHome) {
      requestAnimationFrame(() => setTimeout(() => scrollToId(id), 20));
    } else {
      navigate("/#" + id);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (isHome) {
      requestAnimationFrame(() => setTimeout(() => scrollToId("home"), 20));
    } else {
      navigate("/");
    }
  };

  const goRoute = (to) => {
    setOpen(false);
    navigate(to);
  };

  const linkBase =
    "font-sub uppercase tracking-wider text-sm font-600 text-navy hover:text-court transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-court hover:after:w-full after:transition-all after:duration-300";

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? "bg-white/95 backdrop-blur-md border-b border-navy/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 lg:px-12 flex items-center justify-between h-20">
        <button data-testid="nav-logo" onClick={goHome} className="flex items-center gap-3 group">
          <img src={LOGO} alt="Polisportiva Tricesimo" className="h-11 w-auto object-contain" />
        </button>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_LINKS.map((l) =>
            l.type === "dropdown" ? (
              <DropdownMenu key={l.label}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-dropdown-societa"
                    className={`${linkBase} inline-flex items-center gap-1 outline-none`}
                  >
                    {l.label}
                    <ChevronDown size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="rounded-none border-2 border-navy bg-white p-0 min-w-56"
                >
                  {l.items.map((it) => (
                    <DropdownMenuItem
                      key={it.to}
                      data-testid={`nav-societa-${it.to.replace("/", "")}`}
                      onSelect={() => goRoute(it.to)}
                      className="rounded-none font-sub uppercase tracking-wide text-sm text-navy px-4 py-3 cursor-pointer focus:bg-royal focus:text-white data-[highlighted]:bg-royal data-[highlighted]:text-white"
                    >
                      {it.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => goSection(l.id)}
                className={linkBase}
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <a
          data-testid="nav-cta-desktop"
          href={CTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:inline-flex bg-court text-white font-sub font-bold uppercase tracking-wider px-6 py-3 rounded-none hover:bg-navy transition-colors duration-300"
        >
          Unisciti a noi
        </a>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-navy"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-navy/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) =>
                l.type === "dropdown" ? (
                  <div key={l.label} className="flex flex-col gap-3">
                    <span className="font-sub uppercase tracking-wider text-lg text-navy/50">
                      {l.label}
                    </span>
                    <div className="flex flex-col gap-3 pl-4 border-l-2 border-court">
                      {l.items.map((it) => (
                        <Link
                          key={it.to}
                          to={it.to}
                          data-testid={`mobile-societa-${it.to.replace("/", "")}`}
                          onClick={() => setOpen(false)}
                          className="text-left font-sub uppercase tracking-wide text-base text-navy hover:text-court transition-colors"
                        >
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    key={l.id}
                    data-testid={`mobile-link-${l.id}`}
                    onClick={() => goSection(l.id)}
                    className="text-left font-sub uppercase tracking-wider text-lg text-navy hover:text-court transition-colors"
                  >
                    {l.label}
                  </button>
                )
              )}
              <a
                data-testid="nav-cta-mobile"
                href={CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-2 bg-court text-white font-sub font-bold uppercase tracking-wider px-6 py-3 text-center"
              >
                Unisciti a noi
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
